import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Header } from '../components/Header';
import Silk from '../components/Silk';
import { useWorldIndices } from '../components/useWorldIndices'; 
import { IndexCard } from '../components/IndexCard';
import { IndexFamilyRow } from '../components/IndexFamilyRow';
import { IndexCorrelationLab } from '../components/IndexCorrelationLab';
import { IndicesCandleChart } from '../components/IndicesCandleChart'; // Add this line
import { GeoNewsHero } from '../components/GeoNewsHero';
import { InvestmentTheme } from '../components/InvestmentTheme';
import { ELECTION_CLUSTERS, GLOBAL_MACRO_THESES, useTheses } from '../data/thesisData';
import { IndexTopHoldings } from '../components/IndexTopHoldings';
import { IndexHeroBanner } from '../components/IndexHeroBanner';

const API_URL = process.env.REACT_APP_API_URL || 'http://100.119.212.17:8080';

// --- CONFIGURATION ---
const SESSIONS = {
  ASIA: { 
    name: 'Asian Session', 
    label: 'Tokyo • Hong Kong • Shanghai',
    tickers: ['^N225', '^HSI', '^AXJO', '000001.SS','^KS11','^TWII','^JKSE','^STI','^KLSE'], 
    startHour: 22, endHour: 8 
  },
  EUROPE: { 
    name: 'European Session', 
    label: 'London • Frankfurt • Paris',
    tickers: ['^FTSE', '^GDAXI', '^FCHI', '^STOXX50E'], 
    startHour: 8, endHour: 14 
  },
  AMERICAS: { 
    name: 'American Session', 
    label: 'New York • São Paulo',
    tickers: ['^GSPC', '^DJI', '^IXIC', '^BVSP', 'BTC-USD'], 
    startHour: 14, endHour: 22 
  },
};

const NAMES_MAP = {
  '^N225': 'Nikkei 225',
  '^HSI': 'Hang Seng',
  '^AXJO': 'S&P/ASX 200',
  '000001.SS': 'SSE Composite',
  '^FTSE': 'FTSE 100',
  '^GDAXI': 'DAX Performance',
  '^FCHI': 'CAC 40',
  '^STOXX50E': 'EURO STOXX 50',
  '^GSPC': 'S&P 500',
  '^DJI': 'Dow Jones',
  '^IXIC': 'NASDAQ',
  '^BVSP': 'IBOVESPA',
  'BTC-USD': 'Bitcoin',
  '^KS11':'KOSPI',
  '^TWII':'TWSE Composite',
  '^JKSE':'IDX Composite',
  '^STI':'STI Index',
  '^KLSE':'Bursa Malaysia'
};


const ISO_MAP = {
  '^N225': 'jp',      // Japan
  '^HSI': 'hk',       // Hong Kong
  '^AXJO': 'au',      // Australia
  '000001.SS': 'cn',  // China SSE Composite
  '000300.SS': 'cn',  // China CSI 300
  '^FTSE': 'gb',      // UK
  '^GDAXI': 'de',     // Germany
  '^FCHI': 'fr',      // France
  '^STOXX50E': 'eu',  // Europe
  '^GSPC': 'us',      // USA
  '^DJI': 'us',       // USA
  '^IXIC': 'us',      // USA
  '^NDX': 'us',       // USA Nasdaq 100
  '^RUT': 'us',       // USA Russell 2000
  '^VIX': 'us',       // USA VIX
  '^TNX': 'us',       // USA 10Y Treasury
  '^BVSP': 'br',      // Brazil
  '^KS11': 'kr',      // South Korea
  '^TWII': 'tw',      // Taiwan
  '^JKSE': 'id',      // Indonesia
  '^STI': 'sg',       // Singapore
  '^KLSE': 'my',      // Malaysia
  '^NSEI': 'in',      // India Nifty 50
  '^MXX': 'mx',       // Mexico
  'BTC-USD': 'BITCOIN' // Special handling
};

const HeroCard = ({ session, data, theme }) => {
  const topMover = useMemo(() => {
    if (!data || data.length === 0) return null;
    return data.reduce((prev, current) => 
      (Math.abs(current.changePercent) > Math.abs(prev.changePercent)) ? current : prev
    );
  }, [data]);

  if (!topMover) return (
    <div style={{
      padding: '4rem', 
      textAlign: 'center', 
      background: theme.cardBg, 
      borderRadius: '24px',
      border: `1px solid ${theme.border}`
    }}>
      <div style={{fontSize: '2rem', marginBottom: '1rem'}}>⏳</div>
      <h3 style={{ margin: 0 }}>Waiting for {session.name} Data...</h3>
      <p style={{color: theme.textSecondary}}>Market may be closed or connecting...</p>
    </div>
  );

  const isPos = topMover.changePercent >= 0;
  
  // -- NEW: Determine Flag Code --
  const code = ISO_MAP[topMover.symbol];

  // -- NEW: Render Watermark Helper --
  const renderWatermark = () => {
    if (code === 'BITCOIN') {
      return <span style={{ fontSize: '15rem', filter: 'grayscale(1)', opacity: 0.1 }}>₿</span>;
    }
    if (code) {
      return (
        <img 
          src={`https://flagcdn.com/w640/${code}.png`} // Using higher res for Hero
          alt="flag"
          style={{
            width: '400px',
            height: 'auto',
            transform: 'rotate(-20deg)',
            filter: 'grayscale(0.8)', // Darker grayscale for background blending
            opacity: 0.15,            // Subtle opacity
            display: 'block'
          }}
        />
      );
    }
    return <span style={{ fontSize: '15rem', opacity: 0.1 }}>%</span>;
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(20,20,20,0.8), rgba(40,40,40,0.8))',
      borderRadius: '24px',
      padding: '3rem',
      border: `1px solid ${theme.border}`,
      backdropFilter: 'blur(20px)',
      boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Session Badge */}
      <div style={{
        position: 'absolute', top: '2rem', left: '2rem',
        background: '#D4AF37', color: '#000',
        fontWeight: 'bold', padding: '0.4rem 1rem', borderRadius: '100px',
        fontSize: '0.8rem', letterSpacing: '1px',
        boxShadow: '0 0 15px rgba(212, 175, 55, 0.4)',
        zIndex: 2
      }}>
        LIVE • {session.name.toUpperCase()}
      </div>

      <div style={{ marginTop: '2rem', textAlign: 'center', zIndex: 2, position: 'relative' }}>
        <h2 style={{ fontSize: '1.5rem', color: '#a1a1aa', marginBottom: '0.5rem' }}>
          Top Mover: {NAMES_MAP[topMover.symbol] || topMover.symbol}
        </h2>
        <div style={{ 
          fontSize: 'clamp(4rem, 8vw, 6rem)', 
          fontWeight: '900', 
          fontFamily: 'monospace', 
          lineHeight: '1',
          letterSpacing: '-2px',
          color: '#fff',
          textShadow: '0 10px 30px rgba(0,0,0,0.5)'
        }}>
          {topMover.price?.toLocaleString(undefined, {minimumFractionDigits: 2}) || "0.00"}
        </div>
        <div style={{ 
          fontSize: '2.5rem', 
          fontWeight: 'bold', 
          color: isPos ? '#4ade80' : '#ef4444',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem',
          marginTop: '1rem'
        }}>
          <span>{isPos ? '▲' : '▼'} {Math.abs(topMover.change || 0).toFixed(2)}</span>
          <span style={{ opacity: 0.7, fontSize: '1.5rem' }}>({(topMover.changePercent || 0).toFixed(2)}%)</span>
        </div>
      </div>
      
      {/* New Background Decor (Flag) */}
      <div style={{
        position: 'absolute', 
        right: '-50px', 
        bottom: '-80px', 
        pointerEvents: 'none',
        zIndex: 1
      }}>
        {renderWatermark()}
      </div>
    </div>
  );
};

// ============================================
// INDEX TICKER ROW — self-fetches from :6062 (indices_etfs service)
// Same warm-cached source used by IndexFamilyRow. Weekend-safe.
// IndexCard is untouched; we adapt the shape at the row level.
// ============================================
const INDICES_API = 'http://100.119.212.17:6062';

const IndexTickerRow = ({ theme }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = () => {
      fetch(`${INDICES_API}/api/quotes?class=INDEX`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (!alive || !data || !data.quotes) return;
          const adapted = data.quotes
            .filter(q => q.source === 'yahoo_v2' && q.ult != null && q.ult !== 0)
            .map(q => ({
              // Adapt :6062 shape → IndexCard shape (symbol/price/change/changePercent)
              symbol: q.ticker,
              price: q.ult,
              changePercent: q.var,
              change: (q.ult != null && q.ult_prev != null) ? (q.ult - q.ult_prev) : 0,
              // Passthrough — used for the name fallback below
              _name: q.name,
            }));
          setItems(adapted);
          setLoading(false);
        })
        .catch(() => { if (alive) setLoading(false); });
    };
    load();
    const t = setInterval(load, 180 * 1000); // 3 min — matches backend refresh
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (loading) {
    return (
      <div style={{
        padding: '2rem', textAlign: 'center',
        background: theme.cardBg, borderRadius: '16px',
        border: `1px solid ${theme.border}`,
        color: theme.textSecondary, fontFamily: "'IBM Plex Mono', monospace",
        fontSize: '0.85rem',
      }}>
        Carregando índices via :6062 · indices_etfs ...
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div style={{
        padding: '2rem', textAlign: 'center',
        background: 'rgba(239, 68, 68, 0.05)',
        border: '1px solid rgba(239, 68, 68, 0.2)',
        borderRadius: '16px',
        color: '#fca5a5', fontFamily: "'IBM Plex Mono', monospace",
        fontSize: '0.85rem',
      }}>
        ⚠ indices_etfs offline. Ligue o serviço em <code>:6062</code>.
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      gap: '1.5rem',
      overflowX: 'auto',
      padding: '1.5rem 0.5rem',
      scrollbarWidth: 'none',
      maskImage: 'linear-gradient(to right, transparent, black 2%, black 98%, transparent)',
      WebkitMaskImage: 'linear-gradient(to right, transparent, black 2%, black 98%, transparent)',
    }}>
      {items.map((item) => (
        <IndexCard
          key={item.symbol}
          item={item}
          theme={theme}
          name={NAMES_MAP[item.symbol] || item._name || item.symbol}
        />
      ))}
    </div>
  );
};


// ============================================
// EDITORIAL INDICES — migrated from ThesisPage
// Stock baskets organized by political/macro theme
// ============================================
const EditorialIndices = ({ theme }) => {
  const { electionClusters, macroTheses, loaded } = useTheses();
  const [marketData, setMarketData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/overview`);
        const data = await response.json();
        setMarketData(data);
      } catch (err) {
        console.error('Error fetching market data for editorial indices:', err);
      }
    };
    fetchData();
  }, []);

  if (!loaded) return null;

  return (
    <>
      {/* Eleições 2026 — Bancadas & Políticas */}
      {electionClusters.length > 0 && (
        <div style={{ marginBottom: '4rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <h2 style={{ fontFamily: "'Roble', system-ui, sans-serif", fontSize: '2rem', fontWeight: 800, color: theme.textPrimary }}>
              🗳️ Eleições 2026 • Bancadas & Políticas
            </h2>
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: '0.75rem',
              background: 'rgba(34, 197, 94, 0.2)',
              color: '#22c55e',
              padding: '4px 12px',
              borderRadius: '6px',
              fontWeight: 'bold'
            }}>
              LIVE DATA
            </span>
          </div>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, color: theme.textSecondary, marginBottom: '2rem', maxWidth: '800px' }}>
            Clusters de investimento baseados em cenários eleitorais e mudanças regulatórias esperadas.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 480px), 1fr))', gap: '1.5rem' }}>
            {electionClusters.map((cluster) => (
              <InvestmentTheme 
                key={cluster.id}
                theme={theme}
                title={cluster.title}
                subtitle={cluster.subtitle}
                tickers={cluster.tickers}
                marketData={marketData}
              />
            ))}
          </div>
        </div>
      )}

      {/* Global Macro & Tendências 2026 */}
      {macroTheses.length > 0 && (
        <div style={{ marginBottom: '4rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <h2 style={{ fontFamily: "'Roble', system-ui, sans-serif", fontSize: '2rem', fontWeight: 800, color: theme.textPrimary }}>
              🌎 Global Macro & Tendências 2026
            </h2>
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: '0.75rem',
              background: '#0047AB',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '6px',
              fontWeight: 'bold'
            }}>
              EDITORIAL INDICES
            </span>
          </div>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, color: theme.textSecondary, marginBottom: '2rem', maxWidth: '800px' }}>
            Índices editoriais baseados em temas macro globais. Não são hipóteses de vigilância — são cestas de investimento temáticas.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: '1.5rem' }}>
            {macroTheses.map((macro) => (
              <InvestmentTheme 
                key={macro.id}
                theme={theme}
                title={macro.title}
                subtitle={macro.subtitle}
                tickers={macro.tickers}
                marketData={marketData}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export const WorldIndicesPage = () => {
  const [isDark, setIsDark] = useState(true);
  const { data: wsData, status } = useWorldIndices();
  const [activeSession, setActiveSession] = useState('ASIA');
  const [candles, setCandles] = useState([]);
  const lastProcessedRef = useRef({ time: 0, price: 0, symbol: null });
  // Determine active session based on UTC time
  useEffect(() => {
    const updateSession = () => {
      const h = new Date().getUTCHours();
      if (h >= SESSIONS.ASIA.startHour || h < SESSIONS.ASIA.endHour) setActiveSession('ASIA');
      else if (h >= SESSIONS.EUROPE.startHour && h < SESSIONS.EUROPE.endHour) setActiveSession('EUROPE');
      else setActiveSession('AMERICAS');
    };
    updateSession();
    const t = setInterval(updateSession, 60000); // Check every minute
    return () => clearInterval(t);
  }, []);

  // 2. Identify Top Mover (The "Hero" Asset)
  const sessionTickers = SESSIONS[activeSession].tickers;
  const topMover = useMemo(() => {
    const sessionData = wsData.filter(d => sessionTickers.includes(d.symbol));
    if (sessionData.length === 0) return null;
    return sessionData.reduce((prev, current) => 
      (Math.abs(current.changePercent) > Math.abs(prev.changePercent)) ? current : prev
    );
  }, [wsData, activeSession]);

  // 3a. SEED HISTORICAL CANDLES (real OHLC from the Go proxy) — keyed on symbol only
  // so it fetches once per index, not on every price tick. The chart shows real
  // price history immediately; live ticks (3b) extend it.
  useEffect(() => {
    const sym = topMover?.symbol;
    if (!sym) {
      setCandles([]);
      lastProcessedRef.current = { time: 0, price: 0, symbol: null };
      return;
    }
    let alive = true;
    setCandles([]);
    lastProcessedRef.current = { time: 0, price: 0, symbol: sym };

    fetch(`${API_URL}/api/yahoo/v2/${sym}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (!alive || !d || !d.candles) return;
        const hist = d.candles
          .filter(c => c.close != null)
          .map(c => ({ time: c.ts, open: c.open, high: c.high, low: c.low, close: c.close }))
          .sort((a, b) => a.time - b.time);
        setCandles(hist);
        const last = hist[hist.length - 1];
        lastProcessedRef.current = { time: last ? last.time : 0, price: last ? last.close : 0, symbol: sym };
      })
      .catch(() => {});

    return () => { alive = false; };
  }, [topMover?.symbol]);

  // 3b. LIVE TICK APPEND — extends the seeded history with the WS feed.
  useEffect(() => {
    if (!topMover) return;
    if (lastProcessedRef.current.symbol !== topMover.symbol) return; // wait for seed
    const price = topMover.price;
    if (!price) return;

    const now = Math.floor(Date.now() / 1000);
    const bucketTime = Math.floor(now / 60) * 60;
    // never insert a live candle at or before the last (historical) candle — keeps
    // lightweight-charts' strictly-ascending time requirement happy.
    if (bucketTime < lastProcessedRef.current.time) return;

    setCandles(prevCandles => {
      const newCandles = [...prevCandles];
      const lastCandle = newCandles.length > 0 ? newCandles[newCandles.length - 1] : null;

      if (lastCandle && lastCandle.time === bucketTime) {
        newCandles[newCandles.length - 1] = {
          ...lastCandle,
          high: Math.max(lastCandle.high, price),
          low: Math.min(lastCandle.low, price),
          close: price,
        };
        return newCandles;
      }
      if (lastCandle && bucketTime <= lastCandle.time) return newCandles; // guard
      const newCandle = { time: bucketTime, open: price, high: price, low: price, close: price };
      if (newCandles.length > 600) newCandles.shift();
      return [...newCandles, newCandle];
    });

    lastProcessedRef.current = { ...lastProcessedRef.current, price, time: bucketTime };
  }, [topMover]);


  // Updated Theme to match CryptoPage/Overview
  const theme = {
    background: isDark ? '#080808' : '#f9fafb',
    textPrimary: isDark ? '#fff' : '#111827',
    textSecondary: isDark ? '#9ca3af' : '#6b7280',
    border: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.1)',
    borderHover: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.05)',
    cardBg: isDark ? 'rgba(20, 20, 20, 0.7)' : 'rgba(255, 255, 255, 0.9)',
    tickerBg: isDark ? 'rgba(0,0,0,0.4)' : 'rgba(248, 250, 252, 0.9)',
  };

  // Filter data for the active hero section
  const sessionData = useMemo(() => {
    const targetTickers = SESSIONS[activeSession].tickers;
    return wsData.filter(d => targetTickers.includes(d.symbol));
  }, [wsData, activeSession]);

  // Format for Header Ticker
  const headerTickers = useMemo(() => 
    wsData.map(d => ({
      ticker: d.symbol.replace('^', ''),
      name: NAMES_MAP[d.symbol] || d.symbol,
      ult: d.price,
      var: d.changePercent
    }))
  , [wsData]);

  return (
    <div style={{ minHeight: '100vh', background: theme.background, color: theme.textPrimary, position: 'relative' }}>
      
      {/* Fixed Background - Matching CryptoPage */}
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
        <Silk speed={20} scale={1.2} color={isDark ? "#1A1A2E" : "#E0E7FF"} noiseIntensity={1} />
      </div>
      
      <div style={{ position: 'relative', zIndex: 1 }}>
        <Header theme={theme} isDark={isDark} toggleTheme={() => setIsDark(!isDark)} topTickers={headerTickers} />

        <main style={{ maxWidth: '1400px', margin: '0 auto', padding: '4rem 2rem' }}>
          

          
          {/* Page Header Section */}
          <div style={{ marginBottom: '4rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <h1 style={{ 
                fontSize: '3rem', 
                fontWeight: '900', 
                margin: 0, 
                background: 'linear-gradient(to right, #D4AF37, #FCD34D)', 
                WebkitBackgroundClip: 'text', 
                WebkitTextFillColor: 'transparent' 
              }}>
                Global Indices
              </h1>
              <span style={{ 
                background: 'rgba(212, 175, 55, 0.1)', 
                color: '#D4AF37', 
                padding: '4px 12px', 
                borderRadius: '100px', 
                fontSize: '0.8rem', 
                fontWeight: 'bold',
                border: '1px solid rgba(212, 175, 55, 0.3)'
              }}>
                WEBSOCKET FEED
              </span>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', flexWrap: 'wrap', gap: '2rem' }}>
              <p style={{ color: theme.textSecondary, fontSize: '1.2rem', maxWidth: '600px', margin: 0 }}>
                Monitoramento em tempo real dos principais índices das sessões asiática, europeia e americana.
              </p>

              {/* Session Toggles */}
              <div style={{ display: 'flex', gap: '0.5rem', background: theme.cardBg, padding: '0.5rem', borderRadius: '12px', border: `1px solid ${theme.border}` }}>
                {Object.keys(SESSIONS).map(k => (
                  <button 
                    key={k}
                    onClick={() => setActiveSession(k)}
                    style={{
                      padding: '0.6rem 1.2rem',
                      borderRadius: '8px',
                      border: 'none',
                      background: activeSession === k ? '#D4AF37' : 'transparent',
                      color: activeSession === k ? '#000' : theme.textSecondary,
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      transition: 'all 0.2s',
                      fontSize: '0.9rem'
                    }}
                  >
                    {k}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ═══ HERO: Correlation Lab — cross-asset discovery playground ═══ */}
          <div style={{ marginBottom: '4rem' }}>
            <IndexCorrelationLab />
          </div>
<IndexTopHoldings />

          {/* Connection Status Warning */}
          {status === 'DISCONNECTED' && (
            <div style={{ 
              background: 'rgba(239, 68, 68, 0.1)', 
              color: '#fca5a5', 
              padding: '1.5rem', 
              borderRadius: '12px', 
              textAlign: 'center', 
              marginBottom: '3rem',
              border: '1px solid rgba(239, 68, 68, 0.3)'
            }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>⚠️</div>
              <strong>Bridge Disconnected.</strong><br/> 
              Ensure <code>go run .</code> is running on port <strong>8392</strong>.
            </div>
          )}


          {/* Separator */}
          <div style={{ margin: '4rem 0', borderTop: `1px dashed ${theme.border}` }} />

          {/* Rolling Tickers for ALL Indices */}
          <div style={{ marginBottom: '3rem' }}>
             <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '2rem', fontWeight: '800', color: theme.textPrimary, margin: 0 }}>
                  Visão Geral do Mercado
                </h2>
                <span style={{ fontSize: '0.75rem', background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', border: '1px solid rgba(59, 130, 246, 0.3)', padding: '4px 12px', borderRadius: '100px', fontWeight: 'bold' }}>
                  ALL SESSIONS
                </span>
             </div>
            <IndexTickerRow theme={theme} />
          </div>

          {/* Separator */}
          <div style={{ margin: '4rem 0', borderTop: `1px dashed ${theme.border}` }} />
<IndexHeroBanner />                 
          {/* REST-based family view: works on weekends, ETF cross-listings + ADR spread */}
          <IndexFamilyRow theme={theme} />

              <h1 style={{ 
                fontSize: '3rem', 
                fontWeight: '900', 
                margin: 0, 
                background: 'linear-gradient(to right, #D4AF37, #FCD34D)', 
                WebkitBackgroundClip: 'text', 
                WebkitTextFillColor: 'transparent' 
              }}>
                Editorial Indices
              </h1>
          {/* Editorial Indices — Bancadas & Macro (migrated from ThesisPage) */}
          <div style={{ margin: '4rem 0', borderTop: `1px dashed ${theme.border}` }} />
          <EditorialIndices theme={theme} />

{/* GEOPOLITICS NEWS HERO — what's actually behind the move */}
          <GeoNewsHero theme={theme} />

{/* HERO SECTION: SIDE-BY-SIDE (Chart + Details) */}
          <div className="indices-hero-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem', marginBottom: '4rem', height: '400px' }}>
  
            {/* LEFT: Hero Details */}
            <HeroCard session={SESSIONS[activeSession]} data={sessionData} theme={theme} />

            {/* RIGHT: Candle Chart (historical seed + live ticks) */}
            <div className="indices-hero-chart" style={{ 
              background: theme.cardBg, 
              border: `1px solid ${theme.border}`, 
              borderRadius: '16px', 
              overflow: 'hidden',
              position: 'relative'
            }}>
              {topMover && candles.length > 0 ? (
                <IndicesCandleChart 
                  data={candles} 
                  theme={theme} 
                />
              ) : (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textSecondary }}>
                  {topMover ? 'Carregando histórico...' : 'Waiting for Session Data...'}
                </div>
              )}
              
              {/* Live Indicator */}
              <div style={{ position: 'absolute', top: '16px', left: '20px', display: 'flex', alignItems: 'center', gap: '8px', zIndex: 30 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', animation: 'pulse 1s infinite' }} />
                <span style={{ fontSize: '0.8rem', fontWeight: 'bold', color: theme.textSecondary }}>LIVE 1m</span>
              </div>
            </div>
          </div>

          {/* Separator */}
          <div style={{ margin: '4rem 0', borderTop: `1px dashed ${theme.border}` }} />

          {/* More geopolitics — continuation feed (offset → no repeats) */}
          <GeoNewsHero theme={theme} variant="grid" offset={5} label="Mais Geopolítica" />


        </main>
      </div>
            <style>{`
              @keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.5; } 100% { opacity: 1; } }
              @media (max-width: 768px) {
                .indices-hero-grid { grid-template-columns: 1fr !important; height: auto !important; }
                .indices-hero-chart { height: 400px !important; }
              }
            `}</style>

    </div>
  );
};